import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { assignmentService } from '@/services/assignmentService'
import type {
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
  CreateSubmissionRequest,
  GradeSubmissionRequest,
} from '@/types/assignment'

// Get all assignments with lesson/module/course info
export function useAllAssignments() {
  return useQuery({
    queryKey: ['assignments', 'all'],
    queryFn: async () => {
      const response = await assignmentService.listAllAssignments()
      return response.data
    },
  })
}

// Get assignment by lesson ID
export function useAssignmentByLesson(lessonId: string) {
  return useQuery({
    queryKey: ['assignments', 'lesson', lessonId],
    queryFn: async () => {
      const response = await assignmentService.getAssignmentsByLesson(lessonId)
      // Return first assignment or null (typically a lesson has 0 or 1 assignment)
      return response.data && response.data.length > 0 ? response.data[0] : null
    },
    enabled: !!lessonId,
  })
}

// Get assignment by ID
export function useAssignment(assignmentId: string) {
  return useQuery({
    queryKey: ['assignments', assignmentId],
    queryFn: async () => {
      const response = await assignmentService.getAssignment(assignmentId)
      return response.data
    },
    enabled: !!assignmentId,
  })
}

// Get submissions for an assignment (admin/instructor)
export function useSubmissions(assignmentId: string) {
  return useQuery({
    queryKey: ['submissions', assignmentId],
    queryFn: async () => {
      const response = await assignmentService.getSubmissionsByAssignment(assignmentId)
      return response.data || []
    },
    enabled: !!assignmentId,
  })
}

// Get student's own submissions for an assignment
export function useStudentSubmissions(assignmentId: string, enrollmentId: string) {
  return useQuery({
    queryKey: ['submissions', 'student', assignmentId, enrollmentId],
    queryFn: async () => {
      const response = await assignmentService.getStudentSubmissions(assignmentId, enrollmentId)
      return response.data || []
    },
    enabled: !!assignmentId && !!enrollmentId,
  })
}

// Create a new assignment
export function useCreateAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAssignmentRequest) => assignmentService.createAssignment(data),
    onSuccess: (response) => {
      const assignment = response.data
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      if (assignment) {
        queryClient.invalidateQueries({ queryKey: ['assignments', 'lesson', assignment.lessonId] })
      }
    },
  })
}

// Update an assignment
export function useUpdateAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssignmentRequest }) =>
      assignmentService.updateAssignment(id, data),
    onSuccess: (response) => {
      const assignment = response.data
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      if (assignment) {
        queryClient.invalidateQueries({ queryKey: ['assignments', assignment.id] })
        queryClient.invalidateQueries({ queryKey: ['assignments', 'lesson', assignment.lessonId] })
      }
    },
  })
}

// Delete an assignment
export function useDeleteAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => assignmentService.deleteAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
    },
  })
}

// Publish an assignment
export function usePublishAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => assignmentService.publishAssignment(id),
    onSuccess: (response) => {
      const assignment = response.data
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      if (assignment) {
        queryClient.invalidateQueries({ queryKey: ['assignments', assignment.id] })
        queryClient.invalidateQueries({ queryKey: ['assignments', 'lesson', assignment.lessonId] })
      }
    },
  })
}

// Create a submission
export function useCreateSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      assignmentId,
      data,
    }: {
      assignmentId: string
      data: CreateSubmissionRequest
    }) => assignmentService.createSubmission(assignmentId, data),
    onSuccess: (response) => {
      const submission = response.data
      if (submission) {
        queryClient.invalidateQueries({ queryKey: ['submissions', submission.assignmentId] })
      }
    },
  })
}

// Grade a submission
export function useGradeSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      submissionId,
      data,
    }: {
      submissionId: string
      assignmentId: string
      data: GradeSubmissionRequest
    }) => assignmentService.gradeSubmission(submissionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions', variables.assignmentId] })
    },
  })
}

// Return a submission for revision
export function useReturnSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      submissionId,
      feedback,
    }: {
      submissionId: string
      assignmentId: string
      feedback: string
    }) => assignmentService.returnSubmission(submissionId, feedback),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions', variables.assignmentId] })
    },
  })
}

// Delete a submission (admin/instructor)
export function useDeleteSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      submissionId,
    }: {
      submissionId: string
      assignmentId: string
    }) => assignmentService.deleteSubmission(submissionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions', variables.assignmentId] })
    },
  })
}

// Get all submissions for an enrollment (student grade view)
export function useStudentAssignmentSubmissions(enrollmentId: string) {
  return useQuery({
    queryKey: ['student-assignment-submissions', enrollmentId],
    queryFn: async () => {
      const response = await assignmentService.getSubmissionsByEnrollment(enrollmentId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch submissions')
      }
      return response.data ?? []
    },
    enabled: !!enrollmentId,
  })
}
